import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faCheck, 
    faImage, 
    faVideo, 
    faPodcast, 
    faClock,
    faMagnifyingGlass,
    faFile,
    faSpinner,
    faExclamationCircle,
    faPlay,
    faRotate,
    faCloudArrowUp,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import './GalleryBrowser.css';
import { galleryService } from '../../services/galleryService';

import { useDropzone } from 'react-dropzone';
import { toast, ToastContainer } from 'react-toastify';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import LoadingSpinner from '../Common/LoadingSpinner';


// First, let's create a memoized MediaPreview component
const MediaPreview = React.memo(({ item }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [mediaUrl, setMediaUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const isMounted = useRef(true);

    const isVideo = item.mimeType?.startsWith('video/');
    const isAudio = item.mimeType?.startsWith('audio/');

    // Fallback: Check by file extension if mimeType is not set correctly
    const extension = item.name?.split('.').pop()?.toLowerCase();
    const isAudioByExtension = ['wav', 'mp3', 'm4a', 'ogg', 'flac', 'aac'].includes(extension);
    const isVideoByExtension = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'].includes(extension);

    // Use mimeType first, then fallback to extension
    const finalIsAudio = isAudio || isAudioByExtension;
    const finalIsVideo = isVideo || isVideoByExtension;

    const loadMedia = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Use thumbnail endpoint for images (smaller/faster), full endpoint for other media
            const isImage = !finalIsVideo && !finalIsAudio;
            const endpoint = isImage ? 'getMediaThumbnail' : 'getMediaContent';
            const url = `${process.env.REACT_APP_API_URL}/api/graph/${endpoint}/${item.name}`;

            // Use galleryService cache for deduplication and caching
            const response = await galleryService.getMediaContent(url);

            if (isMounted.current) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setMediaUrl(blobUrl);
            }
        } catch (err) {
            console.error('Error loading media:', err);
            if (isMounted.current) {
                setError('Failed to load media');
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [item.name, finalIsVideo, finalIsAudio]);

    // Cleanup isMounted on unmount only
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Revoke old blob URLs when mediaUrl changes or on unmount
    useEffect(() => {
        return () => {
            if (mediaUrl) {
                URL.revokeObjectURL(mediaUrl);
            }
        };
    }, [mediaUrl]);

    // Reset state when item changes
    useEffect(() => {
        setMediaUrl(null);
        setIsLoading(false);
        setError(null);
        setIsPlaying(false);
    }, [item.name]);

    const handlePlayClick = async (e) => {
        e.stopPropagation();
        if (!mediaUrl) {
            await loadMedia();
        }
        setIsPlaying(true);
    };

    // For images, load when item changes
    useEffect(() => {
        if (!finalIsVideo && !finalIsAudio) {
            loadMedia();
        }
    }, [item.name, finalIsVideo, finalIsAudio, loadMedia]);

    if (error) {
        return (
            <div className="media-preview error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>Error: {error}</span>
            </div>
        );
    }

    return (
        <div className="media-preview">
            {isLoading ? (
                <LoadingSpinner />
            ) : finalIsVideo ? (
                <div className="video-preview">
                    {isPlaying && mediaUrl ? (
                        <video 
                            src={mediaUrl}
                            controls
                            autoPlay
                            className="video-preview-player"
                            onEnded={() => setIsPlaying(false)}
                        />
                    ) : (
                        <div className="video-placeholder">
                            <FontAwesomeIcon icon={faVideo} className="video-icon" />
                            <button 
                                className="play-button"
                                onClick={handlePlayClick}
                            >
                                <FontAwesomeIcon icon={faPlay} /> Play Video
                            </button>
                        </div>
                    )}
                </div>
            ) : finalIsAudio ? (
                <div className="audio-preview">
                    {isPlaying && mediaUrl ? (
                        <audio 
                            src={mediaUrl}
                            controls
                            autoPlay
                            className="audio-preview-player"
                            onEnded={() => setIsPlaying(false)}
                        />
                    ) : (
                        <div className="audio-placeholder">
                            <FontAwesomeIcon icon={faPodcast} className="audio-icon" />
                            <button 
                                className="play-button"
                                onClick={handlePlayClick}
                            >
                                <FontAwesomeIcon icon={faPlay} /> Play Audio
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <img 
                    src={mediaUrl}
                    alt={item.title || item.name}
                    className="preview-thumbnail"
                    onError={() => setError('Failed to load image')}
                />
            )}
        </div>
    );
});

// Update the GalleryItem component to use the new MediaPreview
const GalleryItem = React.memo(({ item, isSelected, onSelect, onDelete }) => {
    const handleClick = useCallback(() => {
        onSelect(item);
    }, [item, onSelect]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation(); // Prevent item selection
        if (window.confirm('Are you sure you want to delete this item? This only removes the item from this gallery, it does not delete the file from the storage.')) {
            onDelete(item.id);
        }
    }, [item.id, onDelete]);

    return (
        <div
            className={`media-preview-item ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
        >
            <MediaPreview item={item} />
            <div className="media-info">
                <h4>{item.title || item.name}</h4>
                {item.description && <p>{item.description}</p>}
            </div>
            <div className="gallery-browser-delete-actions">
                <button 
                    className="gallery-browser-btn-icon" 
                    onClick={handleDelete}
                    title="Delete item"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            </div>
        </div>
    );
}, (prev, next) => {
    return prev.item.id === next.item.id &&
           prev.item.name === next.item.name &&
           prev.isSelected === next.isSelected;
});

const GalleryBrowser = ({ galleries, mediaType, onSelect, onClose, onRefreshGalleries }) => {
    const [selectedGallery, setSelectedGallery] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentFetchId, setCurrentFetchId] = useState(null);
    const [gallerySearchTerm, setGallerySearchTerm] = useState('');
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [currentGalleryId, setCurrentGalleryId] = useState(null);
    const [apiCallCount, setApiCallCount] = useState(0);
    const [itemErrors, setItemErrors] = useState({});
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [showUploadArea, setShowUploadArea] = useState(false);


    // Add a loading state tracker
    const loadingStateRef = useRef({
        galleryId: null,
        timestamp: null
    });

    const normalizeFileName = (name) => name.toLowerCase().trim();

    const fetchGalleryItems = useCallback(async (galleryId) => {
        // Don't fetch if we're already loading this gallery
        if (loadingStateRef.current.galleryId === galleryId && 
            Date.now() - loadingStateRef.current.timestamp < 1000) {
          
            return;
        }

        try {
            setIsLoading(true);
            loadingStateRef.current = {
                galleryId,
                timestamp: Date.now()
            };

            // Use getGalleryItems which has 5-min caching and request deduplication
            const galleryItems = await galleryService.getGalleryItems(galleryId);

            // Only update if this is still the current gallery
            if (loadingStateRef.current.galleryId === galleryId) {
                setItems(galleryItems);
            }
        } catch (error) {
            console.error('Error fetching gallery items:', error);
        } finally {
            if (loadingStateRef.current.galleryId === galleryId) {
                setIsLoading(false);
            }
        }
    }, []);

    // Memoize filteredGalleries more strictly
    const filteredGalleries = useMemo(() => {
        if (!galleries || !mediaType) return [];
        const filtered = galleries.filter(g => g.type?.toLowerCase() === mediaType.toLowerCase());
        
        return filtered;
    }, [galleries, mediaType]); // Only these two dependencies

    // Update the handleGallerySelect to be more defensive
    const handleGallerySelect = useCallback((gallery) => {
        if (!gallery || selectedGallery?.id === gallery.id) {
            return;
        }

        setSelectedGallery(gallery);
        setSelectedItem(null);
        setCurrentPage(1);
        setItems([]); // Clear old items immediately for instant visual feedback

        fetchGalleryItems(gallery.id);
    }, [fetchGalleryItems, selectedGallery]);

    // Memoize the item selection handler
    const handleItemSelect = useCallback((item) => {
        setSelectedItem(prev => prev?.id === item.id ? prev : item);
    }, []); // No dependencies needed

    // Handle item deletion
    const handleDeleteItem = useCallback(async (itemId) => {
        if (!selectedGallery) return;
        
        try {
            setIsLoading(true);
            
            // Delete the item from the gallery
            await galleryService.deleteGalleryItem(selectedGallery.id, itemId);
            
            // Show success notification
            toast.success('Item deleted successfully');
            
            // Refresh gallery items (force refresh to bypass cache after delete)
            const updatedItems = await galleryService.getGalleryItems(selectedGallery.id, true);
            setItems(updatedItems);

            // Clear selected item if it was the deleted one
            if (selectedItem?.id === itemId) {
                setSelectedItem(null);
            }
            
            // Refresh galleries in parent component
            if (onRefreshGalleries) {
                await onRefreshGalleries();
            }
            
        } catch (error) {
            console.error('Error deleting item:', error);
            toast.error('Failed to delete item: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    }, [selectedGallery, selectedItem, onRefreshGalleries]);

    const handleConfirm = () => {
        
        if (selectedItem) {
            onSelect(selectedItem);
            onClose();
        }
    };

    const getIcon = () => {
        switch (mediaType) {
            case 'image': return faImage;
            case 'video': return faVideo;
            case 'podcast': return faPodcast;
            default: return faImage;
        }
    };

    // Add this memoized sorting function
    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            // Reverse the comparison to get newest first
            return dateB - dateA;  // Changed from dateA - dateB
        });
    }, [items]);

    // Then update the filteredItems calculation
    const filteredItems = useMemo(() => {
        const searchTerm = itemSearchTerm.toLowerCase().trim();
        return sortedItems.filter(item => {
            const itemName = (item.name || '').toLowerCase();
            const itemTitle = (item.title || '').toLowerCase();
            return itemName.includes(searchTerm) || itemTitle.includes(searchTerm);
        });
    }, [sortedItems, itemSearchTerm]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    // Update the items grid rendering
    const renderItemsGrid = useMemo(() => {
        if (!selectedGallery) {
            return <div className="placeholder-message">Select a gallery to view items</div>;
        }

        if (paginatedItems.length === 0) {
            return <div className="placeholder-message">No items found</div>;
        }

        return paginatedItems.map((item, index) => (
            <GalleryItem
                key={item.id ? `${item.id}` : `${startIndex + index}-${item.name}`}
                item={item}
                isSelected={selectedItem?.id === item.id}
                onSelect={handleItemSelect}
                onDelete={handleDeleteItem}
            />
        ));
    }, [paginatedItems, selectedGallery, selectedItem?.id, handleItemSelect, handleDeleteItem]);

    // Add this effect to clean up gallery items when component unmounts
    useEffect(() => {

        return () => {
 
            // Clean up any resources
            setItems([]);
            setCurrentGalleryId(null);
        };
    }, [galleries?.length, mediaType]);

    // Add the refresh handler
    const handleRefresh = async () => {
        if (selectedGallery && !isRefreshing) {
            setIsRefreshing(true);
            try {
                await fetchGalleryItems(selectedGallery.id);
            } catch (error) {
                console.error('Refresh failed:', error);
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    // Add this handler for refreshing galleries
    const handleGalleriesRefresh = async () => {
        if (!isRefreshing && onRefreshGalleries) {
            setIsRefreshing(true);
            try {
               
                await onRefreshGalleries(true);
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    // Fix the dropzone configuration with strict validation
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: acceptedFiles => {
            if (!selectedGallery) {
                toast.warning('Please select a gallery first');
                return;
            }
            
            handleFilesSelected(acceptedFiles);
        },
        accept: mediaType === 'image' 
            ? { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] }
            : mediaType === 'video'
            ? { 'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.mkv', '.webm'] }
            : mediaType === 'podcast'
            ? { 'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'] }
            : {},
        // Add custom validation function
        validator: (file) => {
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.split('.').pop();
            
            const allowedExtensions = {
                image: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
                video: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
                podcast: ['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac']
            };
            
            const allowed = allowedExtensions[mediaType] || [];
            
            if (!allowed.includes(fileExtension)) {
                return {
                    code: 'file-invalid-type',
                    message: `${fileExtension.toUpperCase()} files are not allowed in ${mediaType} galleries. Allowed: ${allowed.map(ext => ext.toUpperCase()).join(', ')}`
                };
            }
            
            return null;
        },
        onDropRejected: (rejectedFiles) => {
            const errorMessages = rejectedFiles.map(rejection => {
                const file = rejection.file;
                const errors = rejection.errors.map(error => error.message).join(', ');
                return `• ${file.name}: ${errors}`;
            }).join('\n');
            
            toast.error(`File upload rejected:\n\n${errorMessages}`, {
                autoClose: 8000,
                style: { 
                    whiteSpace: 'pre-line',
                    maxWidth: '500px'
                }
            });
        }
    });

    // Add file handling function
    const handleFilesSelected = (acceptedFiles) => {
        const existingFileNames = items.map(item => item.name.toLowerCase());
        const duplicates = [];
        const newFiles = [];
        const invalidFiles = [];

        // Define allowed file types for each gallery type
        const allowedTypes = {
            image: {
                extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
                mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
            },
            video: {
                extensions: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
                mimeTypes: ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-ms-wmv', 'video/x-matroska', 'video/webm']
            },
            podcast: {
                extensions: ['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac'],
                mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/flac', 'audio/aac']
            }
        };

        acceptedFiles.forEach(file => {
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.split('.').pop();
            const fileMimeType = file.type.toLowerCase();
            
            // Get allowed types for current gallery
            const allowed = allowedTypes[mediaType] || { extensions: [], mimeTypes: [] };
            
            // Check if file type is allowed for this gallery
            const isExtensionAllowed = allowed.extensions.includes(fileExtension);
            const isMimeTypeAllowed = allowed.mimeTypes.some(mime => fileMimeType.startsWith(mime.split('/')[0]));
            
           

            // Reject files that don't match the gallery type
            if (!isExtensionAllowed && !isMimeTypeAllowed) {
                invalidFiles.push({
                    name: file.name,
                    reason: `${fileExtension.toUpperCase()} files are not allowed in ${mediaType} galleries. Allowed types: ${allowed.extensions.map(ext => ext.toUpperCase()).join(', ')}`
                });
                return;
            }

            if (existingFileNames.includes(fileName)) {
                duplicates.push(file);
            } else {
                newFiles.push({
                    id: Math.random().toString(36).substr(2, 9),
                    file,
                    name: file.name,
                    type: mediaType, // Use the gallery's media type instead of guessing
                    preview: URL.createObjectURL(file)
                });
            }
        });

        // Show error messages for invalid files
        if (invalidFiles.length > 0) {
            const errorMessages = invalidFiles.map(f => `• ${f.name}: ${f.reason}`).join('\n');
            toast.error(`File type validation failed:\n\n${errorMessages}`, {
                autoClose: 8000,
                style: { 
                    whiteSpace: 'pre-line',
                    maxWidth: '500px'
                }
            });
        }

        if (duplicates.length > 0) {
            const duplicateNames = duplicates.map(f => f.name).join(', ');
            if (window.confirm(
                `The following files already exist:\n${duplicateNames}\n\nWould you like to rename them?`
            )) {
                const renamedFiles = duplicates.map(file => {
                    const nameParts = file.name.split('.');
                    const ext = nameParts.pop();
                    const baseName = nameParts.join('.');
                    const newName = `${baseName}_${Date.now()}.${ext}`;
                    const renamedFile = new File([file], newName, { type: file.type });
                    
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        file: renamedFile,
                        name: newName,
                        type: mediaType, // Use the gallery's media type instead of guessing
                        preview: URL.createObjectURL(file)
                    };
                });
                setFiles(prev => [...prev, ...newFiles, ...renamedFiles]);
            } else {
                setFiles(prev => [...prev, ...newFiles]);
            }
        } else {
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    // Upload to BOTH SharePoint AND Azure Blob automatically
    const handleUpload = async () => {
        if (!files.length || !selectedGallery) return;
        setUploading(true);

        const updateProgress = (fileId, value) => {
            setUploadProgress(prev => ({
                ...prev,
                [fileId]: Math.min(100, Math.round(value))
            }));
        };

        try {
            for (const file of files) {
                updateProgress(file.id, 0);

                const formData = new FormData();
                formData.append('file', file.file);
                formData.append('galleryId', selectedGallery.id);

                const result = await galleryService.uploadMedia(formData, progress => {
                    updateProgress(file.id, progress * 0.9);
                });

                const storageInfo = result?.storageSource
                    ? ` (${result.storageSource})`
                    : '';

                toast.success(`${file.name} uploaded successfully${storageInfo}`);

                updateProgress(file.id, 100);

                setFiles(prev => prev.filter(f => f.id !== file.id));
            }

            // Refresh gallery items (force refresh to bypass cache after upload)
            const updatedItems = await galleryService.getGalleryItems(selectedGallery.id, true);
            setItems(updatedItems);

            if (onRefreshGalleries) {
                await onRefreshGalleries();
            }

            setShowUploadArea(false);

        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Upload failed: ${error.message}`);
        } finally {
            setUploading(false);
            setUploadProgress({});
        }
    };

    // Add this memoized gallery item renderer
    const renderGalleryItem = useCallback((gallery) => {
        const isSelected = selectedGallery?.id === gallery.id;
        
        const tooltipContent = (
            <div className="gallery-tooltip">
                <h4>{gallery.title || gallery.name}</h4>
                {gallery.description && (
                    <p className="description">{gallery.description}</p>
                )}
                <div className="tooltip-meta">
                    <span className="meta-item">
                        <FontAwesomeIcon icon={faImage} /> {gallery.itemCount || 0} items
                    </span>
                    <span className="meta-item">
                        <FontAwesomeIcon icon={faClock} /> {
                            new Date(gallery.createdAt || Date.now()).toLocaleDateString()
                        }
                    </span>
                </div>
            </div>
        );

        return (
            <Tippy 
                content={tooltipContent}
                theme="custom"
                placement="right"
                delay={[200, 0]}
                arrow={true}
                interactive={true}
                maxWidth={300}
            >
                <div
                    key={gallery.id}
                    className={`gallery-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleGallerySelect(gallery)}
                >
                    <div className="gallery-item-content">
                        <h3>{gallery.title || gallery.name}</h3>
                    </div>
                </div>
            </Tippy>
        );
    }, [selectedGallery?.id, handleGallerySelect]);

    return (
        <div className="gallery-browser-overlay">
            <ToastContainer />
            <div className="gallery-browser-modal">
                <div className="browser-overlay" onClick={onClose}></div>
                <div className="gallery-browser">
                    <div className="browser-header">
                        <h2>
                            <FontAwesomeIcon icon={getIcon()} /> 
                            Select {mediaType === 'podcast' ? 'Audio' : mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
                        </h2>
                        <button className="close-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="browser-content">
                        <div className="gallery-list">
                            <div className="search-container">
                                <div className="search-box">
                                    <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search galleries..."
                                        value={gallerySearchTerm}
                                        onChange={(e) => setGallerySearchTerm(e.target.value)}
                                    />
                                </div>
                                <button 
                                    className="btn-refresh" 
                                    onClick={handleGalleriesRefresh}
                                    disabled={isRefreshing}
                                    title="Refresh galleries"
                                >
                                    <FontAwesomeIcon 
                                        icon={faRotate} 
                                        className={isRefreshing ? 'fa-spin' : ''} 
                                    />
                                </button>
                            </div>
                            <div className="gallery-items">
                                {filteredGalleries.map(gallery => renderGalleryItem(gallery))}
                            </div>
                        </div>

                        <div className="media-content">
                            {selectedGallery && (
                                <div className="search-container">
                                    <div className="search-box">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            value={itemSearchTerm}
                                            onChange={(e) => setItemSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <button 
                                        className="btn-refresh" 
                                        onClick={handleRefresh}
                                        disabled={isLoading || isRefreshing || !selectedGallery}
                                        title="Refresh gallery items"
                                    >
                                        <FontAwesomeIcon 
                                            icon={faRotate} 
                                            className={isRefreshing ? 'fa-spin' : ''} 
                                        />
                                    </button>
                                </div>
                            )}
                            
                            <div className="upload-section">
                                {selectedGallery && (
                                    <button 
                                        className="btn-secondary upload-toggle"
                                        onClick={() => setShowUploadArea(!showUploadArea)}
                                    >
                                        <FontAwesomeIcon icon={showUploadArea ? faTimes : faCloudArrowUp} />
                                        {showUploadArea ? ' Hide Upload' : ' Upload Files'}
                                    </button>
                                )}

                                {showUploadArea && (
                                    <>
                                        <div 
                                            {...getRootProps()} 
                                            className={`dropzone ${isDragActive ? 'active' : ''} ${!selectedGallery ? 'disabled' : ''}`}
                                        >
                                            <input {...getInputProps()} />
                                            <div className="dropzone-content">
                                                <FontAwesomeIcon icon={faCloudArrowUp} size="2x" />
                                                <p>{isDragActive ? 
                                                    'Drop files here...' : 
                                                    `Drag & drop ${mediaType} files here, or click to select`
                                                }</p>
                                                 </div>
                                        </div>
                                        
                                        {files.length > 0 && (
                                            <div className="upload-preview">
                                                <div className="upload-files">
                                                    {files.map(file => (
                                                        <div key={file.id} className="upload-file">
                                                            {file.type === 'image' && (
                                                                <img src={file.preview} alt={file.name} />
                                                            )}
                                                            <div className="file-info">
                                                                <span className="file-name">{file.name}</span>
                                                                {uploadProgress[file.id] !== undefined && (
                                                                    <div className="progress-bar">
                                                                        <div 
                                                                            className="progress" 
                                                                            style={{width: `${uploadProgress[file.id]}%`}}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button 
                                                                onClick={() => {
                                                                    URL.revokeObjectURL(file.preview);
                                                                    setFiles(prev => prev.filter(f => f.id !== file.id));
                                                                }}
                                                                className="remove-file"
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button 
                                                    className="btn-primary upload-button"
                                                    onClick={handleUpload}
                                                    disabled={uploading}
                                                >
                                                    {uploading ? (
                                                        <><FontAwesomeIcon icon={faSpinner} spin /> Uploading...</>
                                                    ) : (
                                                        <><FontAwesomeIcon icon={faCloudArrowUp} /> Upload Files</>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="items-grid">
                                {isLoading ? (
                                    <div className="placeholder-message">
                                        <LoadingSpinner />
                                    </div>
                                ) : renderItemsGrid}
                            </div>
                            {filteredItems.length > itemsPerPage && (
                                <div className="pagination-controls" style={{ marginTop: '1rem', textAlign: 'center' }}>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    <span style={{ margin: '0 1rem' }}>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage >= totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="browser-footer">
                        <button className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            className="btn-primary"
                            onClick={handleConfirm}
                            disabled={!selectedItem}
                        >
                            <FontAwesomeIcon icon={faCheck} /> Select
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GalleryBrowser; 